import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { motion, useAnimationControls } from 'framer-motion';
import { CloseIcon } from '@heroui/shared-icons';
import { NextUI } from '@/components/NextUI';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import useAuth from '@/hooks/useAuth';
import useCloseOnBrowserBack from '@/hooks/useCloseOnBrowserBack';
import { useCheckoutReturn } from '@/features/subscription/hooks/useCheckoutReturn';
import { RootState } from '@/store';
import {
  setPaymentDetails,
  resetPaymentDetails,
  selectPayment,
} from '@/store/slices/paymentSlice';
import useCheckout from '@/features/subscription/hooks/useCheckout';
import usePaddlePricing from '@/features/subscription/hooks/usePaddlePricing';
import { usePayment } from '@/features/subscription/hooks/usePayment';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';
import { checkoutPricingStore } from '@/features/subscription/utils/checkout/checkoutPricingStore';
import { resolveAnalyticsPaywallType } from '@/features/subscription/utils/analyticsPaywall';
import {
  APP_EVENTS,
  COMMON_EVENTS,
  getUserData,
  logHubxEvent,
} from '@/app/providers/analytics';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import { useSingleTrigger } from '@hubxco/hubx-smartlink';
import {
  PAYWALL_EXTENSION_THUMB_COLORS,
  PAYWALL_LINKS,
  PAYWALL_MODAL_WIDTH_CLASS,
  PAYWALL_SUPPORT_EMAIL,
} from '@/features/tool/constants/paywall';
import FullScreenPaywall1Layout from './paywall/FullScreenPaywall1Layout';
import FullScreenPaywallShell from './paywall/FullScreenPaywallShell';
import {
  Paywall1Layout,
  Paywall2Layout,
  Paywall3Layout,
  type CheckoutMethod,
} from './paywall';
import {
  PaywallPdfPage,
  PaywallStep,
  PaywallStepLine,
  PaywallThumbPlaceholder,
} from './paywall/components';
import FullScreenPaywall2Layout from './paywall/FullScreenPaywall2Layout';
import {
  ApplePayIcon,
  CardBrandsIcon,
  GiftIcon,
  GooglePayIcon,
  ReadyCheckIcon,
  RocketIcon,
  WarningIcon,
} from './paywall/icons';
import FullScreenPaywall3Layout from './paywall/FullScreenPaywall3Layout';
import { formatCurrency, formatDate } from './paywall/utils';
import {
  getImmediateChargeAmount,
  getImmediateChargeCurrency,
  getIntervalAdjective,
  getPriceInterval,
  getPricePeriodLabel,
  getRecurringChargeAmount,
  getRecurringChargeCurrency,
  getRecurringPeriodText,
  getRecurringPriceForSelectedPrice,
  getSelectedPaywallPrice,
  getTrialDays,
  getTrialDurationText,
  shouldUseTrialPricing,
} from '@/features/subscription/utils/pricing/paywallPricing';

const PaymentInformationModal = ({
  data,
  hideModal,
}: {
  data?: any;
  hideModal: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { monthlyPrices, weeklyPrices, yearlyPrices } = usePaddlePricing();
  const { isCheckoutOpen, openCheckout, closeCheckout } = useCheckout();
  const { paymentStatus, updatePaymentStatus } = usePayment();
  const { seats } = useAppSelector(selectPayment);
  const typeForUserAccessLevel = useAppSelector(
    (state: RootState) => state.userAccess.typeForUserAccessLevel
  );
  const { files, exportAsFiles } = useAppSelector((state: any) => state.tool);
  const isPaywall1 = data?.paywallVariant === 'paywall1';
  const isPaywall2 = data?.paywallVariant === 'paywall2';
  const isPaywall3 = data?.paywallVariant === 'paywall3';

  const [consentChecked, setConsentChecked] = useState(false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [hasLoggedCheckout, setHasLoggedCheckout] = useState(false);
  const [checkoutMethod, setCheckoutMethod] = useState<CheckoutMethod | null>(
    null
  );
  const shakeControls = useAnimationControls();
  const checkoutLoadStartRef = useRef<number | null>(null);
  const consentRef = useRef<HTMLDivElement | null>(null);
  const checkoutContainerRef = useRef<HTMLDivElement | null>(null);
  const checkoutAttemptRef = useRef<CheckoutMethod | null>(null);

  const paywallType = resolveAnalyticsPaywallType({
    paywallType: typeForUserAccessLevel,
    toolType: data?.toolType,
  });
  const toolSource = data?.toolType
    ? formatAnalyticsSource(data.toolType)
    : undefined;
  const analyticsSource = data?.toolType
    ? buildStructuredAnalyticsSource(data.toolType, data?.originEntryPoint)
    : undefined;

  const selectedPriceCandidate = useMemo(
    () =>
      data?.selectedPriceId
        ? [...weeklyPrices, ...monthlyPrices, ...yearlyPrices].find(
            (price) => price.id === data.selectedPriceId
          )
        : getSelectedPaywallPrice({
            selectedPlan: data?.selectedPlan,
            monthlyPrices,
            weeklyPrices,
            yearlyPrices,
            user,
          }),
    [
      data?.selectedPlan,
      data?.selectedPriceId,
      monthlyPrices,
      weeklyPrices,
      yearlyPrices,
      user,
    ]
  );

  const candidateHasTrial = shouldUseTrialPricing({
    price: selectedPriceCandidate,
    user,
  });
  const [offerSnapshot, setOfferSnapshot] = useState<{
    price: PaddlePrice;
    hasTrial: boolean;
  } | null>(null);
  const selectedRawPrice = offerSnapshot?.price ?? selectedPriceCandidate;
  const hasTrial = offerSnapshot?.hasTrial ?? candidateHasTrial;

  const selectedRecurringPrice = useMemo(
    () =>
      getRecurringPriceForSelectedPrice({
        selectedPrice: selectedRawPrice,
        monthlyPrices,
        weeklyPrices,
        yearlyPrices,
      }),
    [selectedRawPrice, monthlyPrices, weeklyPrices, yearlyPrices]
  );

  const livePricing = useSyncExternalStore(
    checkoutPricingStore.subscribe,
    checkoutPricingStore.getPricing,
    () => null
  );
  const liveMatchesSelection = livePricing?.priceId === selectedRawPrice?.id;

  const immediateAmount = getImmediateChargeAmount(selectedRawPrice);
  const immediateCurrency = getImmediateChargeCurrency(selectedRawPrice);
  const immediateAmountText =
    liveMatchesSelection && livePricing
      ? formatCurrency(livePricing.dueTodayAmount, livePricing.currencyCode)
      : formatCurrency(immediateAmount, immediateCurrency);
  const recurringAmount = getRecurringChargeAmount(selectedRecurringPrice);
  const recurringCurrency = getRecurringChargeCurrency(selectedRecurringPrice);
  const recurringAmountText =
    liveMatchesSelection && livePricing
      ? formatCurrency(livePricing.recurringAmount, livePricing.currencyCode)
      : formatCurrency(recurringAmount, recurringCurrency);
  const trialDurationText = getTrialDurationText(selectedRawPrice);
  const recurringInterval = getPriceInterval(selectedRecurringPrice);
  const recurringAdjective = getIntervalAdjective(recurringInterval);
  const recurringPeriodText = getRecurringPeriodText(selectedRecurringPrice);
  const renewalPriceText = `${recurringAmountText}${getPricePeriodLabel(
    selectedRecurringPrice,
    'month'
  )}`;
  const customPlanTitle =
    typeof selectedRawPrice?.custom_data?.title === 'string'
      ? selectedRawPrice.custom_data.title.trim()
      : '';
  const planName = customPlanTitle || 'PDF Genius Pro';
  const planSubtitle = hasTrial
    ? `${trialDurationText} trial`
    : `${recurringAmountText}${getPricePeriodLabel(selectedRecurringPrice, 'month')}`;
  const dueTodayAmountText = hasTrial
    ? immediateAmountText
    : recurringAmountText;
  const trialTitleSegment = trialDurationText
    ? `${trialDurationText} trial`
    : 'trial';
  const modalTitle = isPaywall1
    ? hasTrial
      ? data?.isOnlyPayment
        ? `Start your ${trialTitleSegment}`
        : `Start your ${trialTitleSegment} and download file instantly`
      : data?.isOnlyPayment
        ? 'Payment Information'
        : 'Download file instantly'
    : hasTrial
      ? data?.isOnlyPayment
        ? `Start your ${trialTitleSegment} now.`
        : `Start your ${trialTitleSegment} and download file instantly.`
      : 'Payment Information';

  const firstChargeDate = useMemo(() => {
    const date = new Date();
    const trialDays = getTrialDays(selectedRawPrice);
    if (hasTrial && trialDays > 0) {
      date.setDate(date.getDate() + trialDays);
    }
    return formatDate(date);
  }, [hasTrial, selectedRawPrice]);

  const previewFile =
    (files && files.length > 0 ? files[0] : null) || data?.file || null;
  const previewProcessedFile =
    (exportAsFiles && exportAsFiles.length > 0 ? exportAsFiles[0] : null) ||
    data?.processedFile ||
    null;
  const documentName =
    previewProcessedFile?.name || previewFile?.name || 'Your document';
  const documentExtension = (
    data?.extension ||
    documentName.split('.').pop() ||
    'pdf'
  )
    .toLowerCase()
    .replace(/^\./, '');
  const isBrowserBackCloseEnabled = isPaywall1 || isPaywall2 || isPaywall3;
  const { requestReturn, runPendingReturn } = useCheckoutReturn({
    isEnabled: !data?.isOnlyPayment && !data?.openedFromProceedPopup,
    file: previewProcessedFile || previewFile,
    fileName: documentName,
    extension: documentExtension,
    toolType: data?.toolType,
    originEntryPoint: data?.originEntryPoint,
  });

  useSingleTrigger(() => {
    logHubxEvent({
      eventName: APP_EVENTS.LND_CHECKOUT,
      ...(toolSource ? { source: toolSource } : {}),
      event_params: analyticsSource ? { source: analyticsSource } : undefined,
    });

    logHubxEvent({
      eventName: COMMON_EVENTS.LND_PAYWALL,
      ...(toolSource ? { source: toolSource } : {}),
      event_params: {
        ...(analyticsSource ? { source: analyticsSource } : {}),
        paywallType,
      } as any,
    });

    if (typeof window !== 'undefined') {
      const dataLayer = ((window as any).dataLayer ??= []);

      dataLayer.push({
        event: 'lnd_paywall_v2',
        internal_use: false,
        user_data: getUserData(user),
        paywallType,
      });
    }
  });

  useEffect(() => {
    updatePaymentStatus('idle');
    return () => {
      dispatch(resetPaymentDetails());
      closeCheckout();
    };
  }, []);

  useEffect(() => {
    if (selectedRawPrice?.id) {
      dispatch(
        setPaymentDetails({
          selectedPriceId: selectedRawPrice.id,
          interval:
            getPriceInterval(selectedRawPrice) === 'year'
              ? 'year'
              : getPriceInterval(selectedRawPrice) === 'week'
                ? 'week'
                : 'month',
          seats: 1,
        })
      );
    }
  }, [selectedRawPrice, dispatch]);

  useEffect(() => {
    if (paymentStatus === 'completed') {
      const timer = setTimeout(() => handleClose(), 1500);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  useEffect(() => {
    checkoutRevealStore.setInert(
      isPaywall2 || isPaywall3 ? false : !consentChecked
    );
  }, [checkoutMethod, consentChecked, isPaywall2, isPaywall3]);

  useEffect(() => {
    if (isPaywall1 || isPaywall2 || isPaywall3 || !checkoutMethod) {
      return;
    }

    checkoutRevealStore.setRevealTarget(checkoutContainerRef.current);
    return () => checkoutRevealStore.setRevealTarget(null);
  }, [checkoutMethod, isPaywall1, isPaywall2, isPaywall3]);

  useEffect(() => {
    if (
      !isCheckoutOpen ||
      paymentStatus !== 'checkout_ready' ||
      hasLoggedCheckout ||
      !selectedRawPrice
    ) {
      return;
    }

    const checkoutLoadDuration = checkoutLoadStartRef.current
      ? Date.now() - checkoutLoadStartRef.current
      : undefined;

    logHubxEvent({
      eventName: COMMON_EVENTS.CHECKOUT_LOADED,
      event_params: {
        amount: checkoutLoadDuration,
        info: 'success',
        paywallType,
        product_id: selectedRawPrice.product_id,
        currency: recurringCurrency,
      },
    });

    setHasLoggedCheckout(true);
  }, [
    hasLoggedCheckout,
    isCheckoutOpen,
    paywallType,
    paymentStatus,
    recurringCurrency,
    selectedRawPrice,
  ]);

  const handleClose = () => {
    const isCancelled = paymentStatus !== 'completed';
    if (isCancelled) {
      data?.onCancel?.();
      requestReturn();
    }
    closeCheckout();
    hideModal();
    if (!isBrowserBackCloseEnabled) {
      runPendingReturn();
    }
  };

  const handleCloseClick = () => {
    logHubxEvent({
      eventName: COMMON_EVENTS.BTN_PAYWALL_CLOSE,
      event_params: { paywallType },
    });
    handleClose();
  };

  useCloseOnBrowserBack(
    handleCloseClick,
    isBrowserBackCloseEnabled,
    runPendingReturn
  );

  const handleConsentChange = (checked: boolean) => {
    setConsentChecked(checked);
    if (checked) setShowConsentError(false);
  };

  const triggerConsentError = () => {
    setShowConsentError(true);
    consentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    shakeControls.start({
      x: [0, -8, 8, -6, 6, -3, 3, 0],
      transition: { duration: 0.4 },
    });
  };

  const openCheckoutFor = (method: CheckoutMethod) => {
    if (
      !user ||
      !selectedRawPrice?.id ||
      checkoutMethod === method ||
      paymentStatus === 'loading' ||
      paymentStatus === 'processing' ||
      paymentStatus === 'verifying'
    ) {
      return;
    }

    checkoutLoadStartRef.current = Date.now();
    setHasLoggedCheckout(false);

    if (isCheckoutOpen) {
      closeCheckout();
    }

    setCheckoutMethod(method);
  };

  useEffect(() => {
    if (
      (!isPaywall1 && !isPaywall2 && !isPaywall3) ||
      checkoutMethod ||
      !user ||
      !selectedRawPrice?.id
    ) {
      return;
    }

    openCheckoutFor('card');
  }, [
    isPaywall1,
    isPaywall2,
    isPaywall3,
    checkoutMethod,
    user,
    selectedRawPrice?.id,
  ]);

  const handleWalletPay = (
    method: Extract<CheckoutMethod, 'google_pay' | 'apple_pay' | 'paypal'>
  ) => {
    if (!consentChecked) {
      triggerConsentError();
      return;
    }

    openCheckoutFor(method);
  };

  const handlePayWithCard = () => {
    if (!consentChecked) {
      triggerConsentError();
      return;
    }

    openCheckoutFor('card');
  };

  useEffect(() => {
    if (
      !checkoutMethod ||
      isCheckoutOpen ||
      !user ||
      !selectedRawPrice?.id ||
      checkoutAttemptRef.current === checkoutMethod ||
      paymentStatus === 'processing' ||
      paymentStatus === 'verifying' ||
      paymentStatus === 'completed'
    ) {
      return;
    }

    const attemptedMethod = checkoutMethod;
    const checkoutPrice = offerSnapshot?.price ?? selectedRawPrice;
    checkoutAttemptRef.current = attemptedMethod;

    if (!offerSnapshot) {
      setOfferSnapshot({ price: checkoutPrice, hasTrial });
    }

    void openCheckout({
      priceId: checkoutPrice.id,
      quantity: seats || 1,
      toolType: data?.toolType,
      paywallType,
      displayMode: 'inline',
      frameInitialHeight:
        isPaywall1 || isPaywall2 || isPaywall3
          ? 560
          : checkoutMethod === 'card'
            ? 450
            : 560,
      ...(!isPaywall1 && !isPaywall2 && !isPaywall3 && checkoutMethod !== 'card'
        ? { allowedPaymentMethods: [checkoutMethod] }
        : {}),
    }).then((opened) => {
      if (!opened) {
        checkoutAttemptRef.current = null;
        setCheckoutMethod((currentMethod) =>
          currentMethod === attemptedMethod ? null : currentMethod
        );
      }
    });
  }, [
    checkoutMethod,
    isCheckoutOpen,
    paymentStatus,
    user,
    selectedRawPrice?.id,
    offerSnapshot,
    hasTrial,
    isPaywall1,
    isPaywall2,
    isPaywall3,
  ]);

  const consentText = (
    <>
      By checking this box and completing your purchase, you agree that{' '}
      {hasTrial ? (
        <>after the end of the {trialDurationText} trial period, </>
      ) : null}
      your provided payment method will be charged {recurringAmountText}{' '}
      automatically each {recurringInterval}{' '}
      {hasTrial
        ? 'unless you cancel before your trial ends.'
        : 'unless you cancel before your next renewal.'}{' '}
      You can cancel your auto-renewing subscription through your online account
      or by emailing{' '}
      <a
        href={`mailto:${PAYWALL_SUPPORT_EMAIL}`}
        className="text-primary-blue underline underline-offset-2"
      >
        {PAYWALL_SUPPORT_EMAIL}
      </a>{' '}
      before your next {recurringAdjective} renewal date. You also agree to our{' '}
      <Link
        href={PAYWALL_LINKS.subscription}
        target="_blank"
        className="text-primary-blue underline underline-offset-2"
      >
        Subscription Policy
      </Link>
      ,{' '}
      <Link
        href={PAYWALL_LINKS.refund}
        target="_blank"
        className="text-primary-blue underline underline-offset-2"
      >
        Refund Policy
      </Link>
      ,{' '}
      <Link
        href={PAYWALL_LINKS.terms}
        target="_blank"
        className="text-primary-blue underline underline-offset-2"
      >
        Terms and Conditions
      </Link>
      , and{' '}
      <Link
        href={PAYWALL_LINKS.privacy}
        target="_blank"
        className="text-primary-blue underline underline-offset-2"
      >
        Privacy Policy
      </Link>
      .
    </>
  );

  if (isPaywall3) {
    if (data?.isFullScreen) {
      return (
        <FullScreenPaywallShell
          className="bg-[#F6F7F8]"
          onClose={handleCloseClick}
        >
          <FullScreenPaywall3Layout
            isOnlyPayment={Boolean(data?.isOnlyPayment)}
            hasTrial={hasTrial}
            trialDurationText={trialDurationText}
            dueTodayAmountText={dueTodayAmountText}
            recurringAmountText={recurringAmountText}
            recurringPeriodText={recurringPeriodText}
            recurringAdjective={recurringAdjective}
            renewalPriceText={renewalPriceText}
            documentName={documentName}
            documentExtension={documentExtension}
            documentFile={previewProcessedFile || previewFile}
            checkoutMethod={checkoutMethod}
            checkoutContainerRef={checkoutContainerRef}
            paymentStatus={paymentStatus}
          />
        </FullScreenPaywallShell>
      );
    }

    const paywall = (
      <Paywall3Layout
        isOnlyPayment={Boolean(data?.isOnlyPayment)}
        hasTrial={hasTrial}
        trialDurationText={trialDurationText}
        dueTodayAmountText={dueTodayAmountText}
        recurringAmountText={recurringAmountText}
        recurringPeriodText={recurringPeriodText}
        recurringAdjective={recurringAdjective}
        renewalPriceText={renewalPriceText}
        documentName={documentName}
        documentExtension={documentExtension}
        documentFile={previewProcessedFile || previewFile}
        checkoutMethod={checkoutMethod}
        checkoutContainerRef={checkoutContainerRef}
        paymentStatus={paymentStatus}
      />
    );

    if (data?.renderInline) {
      return paywall;
    }

    return (
      <NextUI.Modal
        className={PAYWALL_MODAL_WIDTH_CLASS}
        classNames={{
          content:
            'max-h-[95vh] overflow-y-auto overscroll-y-none bg-[#F6F7F8] p-0',
        }}
        defaultOpen={true}
        name="Paywall3"
        backdrop="blur"
        scrollBehavior="inside"
        hideCloseButton={true}
        onClose={handleCloseClick}
      >
        {paywall}
      </NextUI.Modal>
    );
  }

  if (isPaywall2) {
    if (data?.isFullScreen) {
      return (
        <FullScreenPaywallShell
          className="bg-[#F6F7F8]"
          onClose={handleCloseClick}
        >
          <FullScreenPaywall2Layout
            isOnlyPayment={Boolean(data?.isOnlyPayment)}
            hasTrial={hasTrial}
            trialDurationText={trialDurationText}
            dueTodayAmountText={dueTodayAmountText}
            recurringAmountText={recurringAmountText}
            recurringPeriodText={recurringPeriodText}
            documentName={documentName}
            documentExtension={documentExtension}
            documentFile={previewProcessedFile || previewFile}
            checkoutMethod={checkoutMethod}
            checkoutContainerRef={checkoutContainerRef}
            paymentStatus={paymentStatus}
          />
        </FullScreenPaywallShell>
      );
    }

    const paywall = (
      <Paywall2Layout
        isOnlyPayment={Boolean(data?.isOnlyPayment)}
        hasTrial={hasTrial}
        trialDurationText={trialDurationText}
        dueTodayAmountText={dueTodayAmountText}
        recurringAmountText={recurringAmountText}
        recurringPeriodText={recurringPeriodText}
        documentName={documentName}
        documentExtension={documentExtension}
        documentFile={previewProcessedFile || previewFile}
        checkoutMethod={checkoutMethod}
        checkoutContainerRef={checkoutContainerRef}
        paymentStatus={paymentStatus}
      />
    );

    if (data?.renderInline) {
      return paywall;
    }

    return (
      <NextUI.Modal
        className={PAYWALL_MODAL_WIDTH_CLASS}
        classNames={{
          content:
            'max-h-[95vh] overflow-y-auto overscroll-y-none bg-[#F6F7F8] p-0',
        }}
        defaultOpen={true}
        name="Paywall2"
        backdrop="blur"
        scrollBehavior="inside"
        hideCloseButton={true}
        onClose={handleCloseClick}
      >
        {paywall}
      </NextUI.Modal>
    );
  }

  if (isPaywall1) {
    if (data?.isFullScreen) {
      return (
        <FullScreenPaywallShell className="bg-white" onClose={handleCloseClick}>
          <FullScreenPaywall1Layout
            title={modalTitle}
            isOnlyPayment={Boolean(data?.isOnlyPayment)}
            hasTrial={hasTrial}
            planName={planName}
            planSubtitle={planSubtitle}
            dueTodayAmountText={dueTodayAmountText}
            recurringAmountText={recurringAmountText}
            firstChargeDate={firstChargeDate}
            recurringAdjective={recurringAdjective}
            documentName={documentName}
            documentExtension={documentExtension}
            documentFile={previewProcessedFile || previewFile}
            consentChecked={consentChecked}
            showConsentError={showConsentError}
            consentText={consentText}
            onConsentChange={handleConsentChange}
            checkoutMethod={checkoutMethod}
            checkoutContainerRef={checkoutContainerRef}
            paymentStatus={paymentStatus}
            consentRef={consentRef}
            shakeControls={shakeControls}
            onConsentRequired={triggerConsentError}
          />
        </FullScreenPaywallShell>
      );
    }

    const paywall = (
      <Paywall1Layout
        title={modalTitle}
        isOnlyPayment={Boolean(data?.isOnlyPayment)}
        hasTrial={hasTrial}
        planName={planName}
        planSubtitle={planSubtitle}
        dueTodayAmountText={dueTodayAmountText}
        recurringAmountText={recurringAmountText}
        firstChargeDate={firstChargeDate}
        recurringAdjective={recurringAdjective}
        documentName={documentName}
        documentExtension={documentExtension}
        documentFile={previewProcessedFile || previewFile}
        consentChecked={consentChecked}
        showConsentError={showConsentError}
        consentText={consentText}
        onConsentChange={handleConsentChange}
        checkoutMethod={checkoutMethod}
        checkoutContainerRef={checkoutContainerRef}
        paymentStatus={paymentStatus}
        consentRef={consentRef}
        shakeControls={shakeControls}
        onConsentRequired={triggerConsentError}
      />
    );

    if (data?.renderInline) {
      return paywall;
    }

    return (
      <NextUI.Modal
        className={PAYWALL_MODAL_WIDTH_CLASS}
        classNames={{
          content:
            'max-h-[95vh] overflow-y-auto overscroll-y-none bg-white p-0',
        }}
        defaultOpen={true}
        name="Paywall1"
        backdrop="blur"
        scrollBehavior="inside"
        hideCloseButton={true}
        onClose={handleCloseClick}
      >
        {paywall}
      </NextUI.Modal>
    );
  }

  return (
    <NextUI.Modal
      className="!mx-[20px] xl:max-w-[1340px] max-w-[1000px] max-lg:w-full w-[95vw] max-h-[95vh] !my-0"
      classNames={{
        content: 'max-lg:px-4 p-6 lg:p-10 !overflow-y-auto overscroll-y-none',
      }}
      defaultOpen={true}
      name="PaymentInformation"
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={true}
      onClose={handleCloseClick}
    >
      <div className="flex flex-col bg-white">
        {/* Header */}
        <div>
          <div className="hidden items-start justify-between lg:flex">
            <h2 className="text-[24px] lg:text-[28px] font-bold text-[#292A33]">
              {modalTitle}
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={handleCloseClick}
              className="mt-1 text-[1.25rem] text-[#6D6D6D]"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="lg:hidden">
            <div className="relative flex min-h-10 items-start justify-center">
              <div className="flex flex-col items-center gap-1">
                <span className="text-[18px] font-semibold leading-[1.2] text-[#292A33]">
                  Payment Details
                </span>
                {!data?.isOnlyPayment && (
                  <span className="text-[12px] leading-none text-[#9A9A9A]">
                    Step 2 of 3
                  </span>
                )}
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={handleCloseClick}
                className="absolute right-0 top-0 flex h-6 w-6 items-center justify-end text-[#292A33]"
              >
                <CloseIcon />
              </button>
            </div>
            <h2 className="mt-4 text-[28px] font-bold leading-[1.05] text-[#292A33]">
              {modalTitle}
            </h2>
          </div>
        </div>

        {/* Step indicator */}
        {!data?.isOnlyPayment && (
          <div className="hidden lg:flex items-center gap-3 mt-5 mb-8 text-[15px]">
            <PaywallStep state="complete" label="Document is ready" />
            <PaywallStepLine />
            <PaywallStep state="active" index={2} label="Payment details" />
            <PaywallStepLine />
            <PaywallStep state="inactive" index={3} label="Download" />
          </div>
        )}

        {/* Content */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mt-6 lg:mt-0">
          {/* Payment Details card */}
          <div className="w-full lg:flex-1 rounded-xl border border-[#ECECEC] p-6 lg:p-8">
            <div className="flex items-center gap-2">
              <h3 className="text-[18px] font-semibold text-[#292A33]">
                Payment Details
              </h3>
              <span className="inline-flex items-center gap-0.5 rounded-md border border-[#D3F2E8] bg-[#E3FFF6] p-1.5 text-[12px] font-medium text-[#00A170]">
                <RocketIcon className="w-3.5 h-3.5" />
                Most Popular
              </span>
            </div>

            <motion.div
              ref={consentRef}
              animate={shakeControls}
              className="mt-6"
            >
              {showConsentError && (
                <div className="mb-3 flex items-center gap-2 text-[#F43F3F]">
                  <WarningIcon className="h-[18px] w-[18px] shrink-0" />
                  <span className="text-[14px]">
                    You must check the box below
                  </span>
                </div>
              )}
              <label className="flex cursor-pointer gap-3">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => handleConsentChange(e.target.checked)}
                  className={clsx(
                    'mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border accent-primary-blue',
                    showConsentError ? 'border-[#F43F3F]' : 'border-[#CECFD4]'
                  )}
                />
                <span className="text-[14px] leading-5 text-[#292A33]">
                  {consentText}
                </span>
              </label>
            </motion.div>

            <div
              className={clsx(
                'mt-6 flex flex-col',
                isPaywall1 ? 'gap-2' : 'gap-2.5'
              )}
            >
              {isPaywall1 && (
                <button
                  type="button"
                  onClick={() => handleWalletPay('google_pay')}
                  className="flex h-10 w-full items-center justify-center gap-1 rounded-[10px] bg-black text-[17px] font-medium text-white transition-opacity hover:opacity-90"
                >
                  <GooglePayIcon className="h-6 w-6" />
                  <span>Pay</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => handleWalletPay('apple_pay')}
                className={clsx(
                  'flex w-full items-center justify-center bg-black text-white transition-opacity hover:opacity-90',
                  isPaywall1
                    ? 'h-10 gap-1 rounded-[10px] text-[17px] font-medium'
                    : 'h-12 gap-1.5 rounded-lg text-[15px] font-medium'
                )}
              >
                <ApplePayIcon className={isPaywall1 ? 'h-6 w-6' : 'h-4 w-4'} />
                <span>Pay</span>
              </button>
              {isPaywall1 && (
                <button
                  type="button"
                  onClick={() => handleWalletPay('paypal')}
                  className="flex h-10 w-full items-center justify-center rounded-[10px] bg-black text-[17px] font-semibold italic text-white transition-opacity hover:opacity-90"
                >
                  PayPal
                </button>
              )}
              <button
                type="button"
                onClick={handlePayWithCard}
                className={clsx(
                  'flex w-full text-white transition-opacity hover:opacity-90',
                  isPaywall1
                    ? 'h-12 items-center justify-between rounded-[10px] bg-[#575757] px-6 text-[19px] font-semibold'
                    : 'h-[62px] flex-col items-center justify-center gap-2 rounded-lg bg-[#5F5F5F] px-5 text-[15px] font-medium sm:h-12 sm:flex-row sm:justify-between sm:gap-0'
                )}
              >
                <span>{isPaywall1 ? 'Card' : 'Pay with card'}</span>
                <CardBrandsIcon />
              </button>
            </div>

            {checkoutMethod && (
              <div className="relative mt-6">
                {paymentStatus === 'loading' && (
                  <div className="flex h-[120px] items-center justify-center">
                    <NextUI.Spinner />
                  </div>
                )}
                {paymentStatus === 'verifying' && (
                  <div className="flex h-[120px] flex-col items-center justify-center gap-2 text-center">
                    <NextUI.Spinner />
                    <span className="text-[15px] text-[#6D6D6D]">
                      We are checking your payment status...
                    </span>
                  </div>
                )}
                <div
                  ref={checkoutContainerRef}
                  style={{
                    minHeight: checkoutMethod === 'card' ? 450 : 560,
                  }}
                />
                {checkoutMethod === 'card' && !consentChecked && (
                  <button
                    type="button"
                    aria-label="You must check the box above to subscribe"
                    onClick={triggerConsentError}
                    className="absolute inset-0 z-10 cursor-pointer bg-transparent"
                  />
                )}
              </div>
            )}
          </div>

          {/* Order Summary card */}
          <div className="w-full lg:flex-1 rounded-xl border border-[#ECECEC] p-6 lg:p-8">
            <h3 className="text-[18px] font-semibold text-[#292A33]">
              Order Summary
            </h3>

            {/* Document */}
            {!data?.isOnlyPayment && (
              <div className="mt-4 flex items-center gap-4">
                <DocumentThumb
                  file={previewProcessedFile || previewFile}
                  extension={documentExtension}
                />
                <div className="flex min-w-0 flex-col gap-3">
                  <span className="truncate text-[16px] text-[#292A33] lg:text-[18px]">
                    {documentName}
                  </span>
                  <span className="inline-flex max-w-full self-start items-center gap-1.5 rounded-xl border border-[#D3F2E8] bg-[#E3FFF6] p-2.5 text-[14px] leading-[1.35] text-[#00A170] lg:text-[16px]">
                    <ReadyCheckIcon className="h-[18px] w-[18px] shrink-0" />
                    <span className="min-w-0 break-words">
                      Your document is ready!
                    </span>
                  </span>
                </div>
              </div>
            )}

            <Divider />

            {/* Plan */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[16px] font-semibold text-[#292A33]">
                  {planName}
                </span>
                <span className="text-[14px] text-[#6D6D6D]">
                  {planSubtitle}
                </span>
              </div>
              <GiftIcon className="h-[46px] w-[46px]" />
            </div>

            <Divider />

            {/* Due today */}
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                <span className="text-[16px] font-semibold text-[#292A33]">
                  Due Today
                </span>
                <span className="text-[14px] text-[#6D6D6D]">
                  {hasTrial ? 'Total due today' : 'Charged today'}
                </span>
              </div>
              <span className="text-[16px] font-semibold text-[#292A33]">
                {dueTodayAmountText}
              </span>
            </div>

            <Divider />

            {/* First charge */}
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6D6D6D]">
                First {recurringAdjective} charge {firstChargeDate}
              </span>
              <span className="text-[16px] font-semibold text-[#292A33]">
                {recurringAmountText}
              </span>
            </div>

            <Divider />

            {/* Billing */}
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6D6D6D]">Billing period</span>
              <span className="text-[15px] font-semibold text-[#292A33] text-right">
                Auto-renews {recurringAdjective} until cancelled
              </span>
            </div>

            <Divider />

            {/* Cancellation */}
            <div className="flex items-center justify-between">
              <span className="text-[14px] text-[#6D6D6D]">Cancel</span>
              <span className="text-[15px] font-semibold text-[#292A33]">
                Through your account
              </span>
            </div>
          </div>
        </div>
      </div>
    </NextUI.Modal>
  );
};

const Divider = () => <div className="my-5 h-px w-full bg-[#ECECEC]" />;

const DocumentThumb = ({
  file,
  extension,
}: {
  file?: File | null;
  extension: string;
}) => {
  const badge =
    PAYWALL_EXTENSION_THUMB_COLORS[extension] ?? 'bg-gray-400 border-gray-500';
  const isPdf = file?.type === 'application/pdf';
  const isImage = Boolean(file?.type?.startsWith('image/'));
  const imageUrl = useMemo(
    () => (file && isImage ? URL.createObjectURL(file) : ''),
    [file, isImage]
  );

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  return (
    <div className="relative flex h-[100px] w-[100px] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#EBECF1]">
      {isPdf && file ? (
        <div className="relative z-0 h-[86px] w-[62px] overflow-hidden rounded-lg border border-[#E3E3E3] bg-white">
          <PaywallPdfPage
            file={file}
            width={62}
            fallback={
              <PaywallThumbPlaceholder className="h-[86px] w-[62px] rounded-lg border-[#E3E3E3]" />
            }
          />
        </div>
      ) : isImage && imageUrl ? (
        <img
          src={imageUrl}
          alt={extension}
          className="relative z-0 h-[86px] w-[62px] rounded-lg border border-[#E3E3E3] bg-white object-cover"
        />
      ) : (
        <PaywallThumbPlaceholder className="h-[86px] w-[62px] rounded-lg border-[#E3E3E3]" />
      )}
      <span
        className={clsx(
          'pointer-events-none absolute bottom-[6px] right-[6px] z-10 rounded-lg border px-2.5 py-1 text-[12px] font-semibold uppercase leading-4 text-white',
          badge
        )}
      >
        {extension}
      </span>
    </div>
  );
};

export default PaymentInformationModal;
